import React from 'react';
import { useTranslation } from 'react-i18next';
import { useGetOnDemandCirclesQuery } from '@modernhealth/circles-graphql/v2';

import { useLanguageCode } from '../hooks/useLanguageCode';
import { languageCodes } from '../utils/languageCodes';

import { CircleOnDemandCard, CircleOnDemandCardSkeleton } from './CircleOnDemandCard';
import { CircleOnDemandRow } from './CircleOnDemandRow';
import { CirclesErrorFallback } from './CirclesErrorFallback';

export const CircleLatestOnDemandRowSkeleton = ({ header }: { header: string }) => {
    return (
        <CircleOnDemandRow header={header}>
            <CircleOnDemandCardSkeleton />
            <CircleOnDemandCardSkeleton />
            <CircleOnDemandCardSkeleton />
        </CircleOnDemandRow>
    );
};

const englishLanguageCode = ['en'];

export const CircleLatestOnDemandRow = () => {
    const { t } = useTranslation('circles');

    const languageCode = useLanguageCode();
    const supportedLanguageCode = languageCodes.find(lng => lng === languageCode);

    const { data, loading, refetch } = useGetOnDemandCirclesQuery({
        variables: {
            first: 3,
            filterByLanguageCodes: supportedLanguageCode
                ? [supportedLanguageCode]
                : englishLanguageCode,
        },
    });

    if (loading && !data?.circleOnDemandConnection) {
        return <CircleLatestOnDemandRowSkeleton header={t('sections.on_demand.header')} />;
    }

    if (data?.circleOnDemandConnection.__typename !== 'CircleOnDemandConnection') {
        return <CirclesErrorFallback onRetry={refetch} />;
    }

    const edges = data.circleOnDemandConnection.edges;

    return (
        <CircleOnDemandRow
            to="/circles/on-demand"
            header={t('sections.on_demand.header')}
            cta={t('sections.series.view_all')}
        >
            {edges.map((edge, i) => {
                const ondemand = edge?.node;
                if (!ondemand) return null;

                return (
                    <CircleOnDemandCard
                        seconds={ondemand.video.durationInSeconds}
                        host={ondemand.facilitators[0]?.name}
                        languageCode={ondemand.languageCode}
                        thumb={ondemand.video.thumbnail}
                        key={`${ondemand.id}-${i}`}
                        name={ondemand.name}
                        slug={ondemand.slug}
                    />
                );
            })}
        </CircleOnDemandRow>
    );
};
